import { useMemo } from "react";
import { useActionSets, useMatchSets } from "../api/queries";
import { useEntityRegistry } from "../api/registry";
import type { NameResolvers } from "../logic/auto-transaction-summary";

/**
 * Display-name resolvers for everything a rule can cite by id: the entity
 * registry plus the two library kinds.
 *
 * The rule summaries take these so a cited set reads as its name. An id that
 * doesn't resolve falls through to the raw id, which keeps the summary legible
 * while the queries are still loading — and, for a genuinely dangling
 * reference, is the same thing the `problems` badge is already complaining
 * about.
 */
export const useRuleSetNames = (): NameResolvers => {
  // Hidden included on purpose: a rule flagged *because* an entity was hidden
  // must still be able to name it.
  const { data: reg } = useEntityRegistry(true);
  const matchSets = useMatchSets();
  const actionSets = useActionSets();

  return useMemo(() => {
    const byId = <T extends { id: string; name: string }>(
      sets: T[] | undefined,
    ) => {
      const map = new Map((sets ?? []).map((s) => [s.id as string, s.name]));
      return (id: string) => map.get(id);
    };

    return {
      source: byId(Object.values(reg?.sources ?? {})),
      group: byId(Object.values(reg?.groups ?? {})),
      category: byId(Object.values(reg?.categories ?? {})),
      expenseType: byId(Object.values(reg?.expenseTypes ?? {})),
      matchSet: byId(matchSets.data),
      actionSet: byId(actionSets.data),
    };
  }, [reg, matchSets.data, actionSets.data]);
};
